/**
 * connectionHealthDisplay — the one portal helper for connection health.
 * v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2 (H2-3, H2-6, H2-7; F16, F17, F82, F89): NEW.
 * v4.23 - 2026-10-03 - CONNECTOR-HEALTH-STATE-H4 (H4-R2): the readiness reason WORKFLOW_NOT_ELIGIBLE (onboarding
 *   not far enough along) is a non-retry reason with its own message.
 * v4.26 - 2026-10-06 - INDUSTRY-CODE-NOT-STORED (S8; F11): the readiness reason INDUSTRY_CODE_MISSING (no stored
 *   industry code) is a non-retry reason with its own message; the callers route it to the industry screen.
 *
 * The server computes one verdict per connection (d75 connection_health_view): `healthState` and, for
 * `needs_attention`, `healthReason`. Every portal surface — the Data Sources chip, the Connect page badge and
 * caption, the Connect New Source dialog, the manual-sync messages and the Finish Setup / PipelineProgress
 * messages — renders from this module. The only green rendering is the `healthy` branch; any value outside the
 * HealthState union renders neutral. Customer copy is plan v1 PD-9 (owner-confirmed), verbatim.
 */

export type HealthState = 'healthy' | 'syncing' | 'setting_up' | 'needs_attention' | 'disconnected' | 'unknown';

export const HEALTH_STATES: readonly HealthState[] = [
  'healthy', 'syncing', 'setting_up', 'needs_attention', 'disconnected', 'unknown',
];

export type HealthChipColor = 'success' | 'info' | 'error' | 'default';

export interface HealthDisplay {
  state: HealthState;
  label: string;
  color: HealthChipColor;
  reason: string | null;
}

/** Any connection-shaped object carrying the server verdict. */
export interface HealthCarrier {
  id: string;
  sourceType?: string | null;
  healthState?: unknown;
  healthReason?: string | null;
}

function assertNever(value: never): never {
  throw new Error(`unhandled HealthState: ${String(value)}`);
}

export function normalizeHealthState(value: unknown): HealthState {
  return typeof value === 'string' && (HEALTH_STATES as readonly string[]).includes(value)
    ? (value as HealthState)
    : 'unknown';
}

function displayFor(state: HealthState): Omit<HealthDisplay, 'reason' | 'state'> {
  switch (state) {
    case 'healthy':
      return { label: 'Synced', color: 'success' };
    case 'syncing':
      return { label: 'Syncing', color: 'info' };
    case 'setting_up':
      return { label: 'Setting up', color: 'info' };
    case 'needs_attention':
      return { label: 'Needs attention', color: 'error' };
    case 'disconnected':
      return { label: 'Disconnected', color: 'default' };
    case 'unknown':
      return { label: 'Status unknown', color: 'default' };
    default:
      return assertNever(state); // compile-time exhaustiveness: a new HealthState fails the build here
  }
}

/** Label, chip colour and (for needs_attention only) the reason for one verdict. */
export function healthDisplay(state: unknown, reason?: string | null): HealthDisplay {
  const s = normalizeHealthState(state);
  return { state: s, ...displayFor(s), reason: s === 'needs_attention' ? (reason ?? null) : null };
}

// ---------------------------------------------------------------------------------------------------------
// List freshness (H2-7)
// ---------------------------------------------------------------------------------------------------------

export const LIST_POLL_INTERVAL_MS = 15000;

export function isInProgress(state: unknown): boolean {
  const s = normalizeHealthState(state);
  return s === 'syncing' || s === 'setting_up';
}

/** Poll the list while any listed connection is syncing or setting up (H2-7a). */
export function shouldPollList(connections: ReadonlyArray<{ healthState?: unknown }>): boolean {
  return connections.some(c => isInProgress(c.healthState));
}

/** The refresh action is disabled for a syncing row or the row whose manual sync is in flight (H2-7d). */
export function isRefreshDisabled(connection: { id: string; healthState?: unknown }, syncingConnectionId: string | null): boolean {
  return syncingConnectionId === connection.id || normalizeHealthState(connection.healthState) === 'syncing';
}

// ---------------------------------------------------------------------------------------------------------
// Connect New Source dialog (H2-3c)
// ---------------------------------------------------------------------------------------------------------

export type DialogAction = { kind: 'connect' } | { kind: 'reconnect'; connectionId: string } | { kind: 'disabled' };

/**
 * A type with no listed connection can be connected. A type whose connection needs attention or is
 * disconnected is re-connectable through that connection (Disconnect confirmation first). healthy, syncing,
 * setting_up and unknown stay disabled.
 */
export function dialogActionFor(connectorType: string, connections: ReadonlyArray<HealthCarrier>): DialogAction {
  const existing = connections.find(c => (c.sourceType || '').toLowerCase() === connectorType.toLowerCase());
  if (!existing) return { kind: 'connect' };
  const s = normalizeHealthState(existing.healthState);
  if (s === 'needs_attention' || s === 'disconnected') return { kind: 'reconnect', connectionId: existing.id };
  return { kind: 'disabled' };
}

// ---------------------------------------------------------------------------------------------------------
// Manual sync (H2-3d, H2-7c)
// ---------------------------------------------------------------------------------------------------------

export const SYNC_DID_NOT_FINISH_MESSAGE = 'The sync did not finish. The status on the connection shows what happened.';
export const SYNC_RUNNING_MESSAGE = 'Sync is running. This page updates when it finishes.';

/** A browser timeout (axios ECONNABORTED at 30 s) means the sync is still running; anything else did not finish. */
export function manualSyncFailure(err: unknown): { severity: 'info' | 'error'; message: string } {
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  if (code === 'ECONNABORTED') return { severity: 'info', message: SYNC_RUNNING_MESSAGE };
  return { severity: 'error', message: SYNC_DID_NOT_FINISH_MESSAGE };
}

// ---------------------------------------------------------------------------------------------------------
// Finish Setup / PipelineProgress (H2-6)
// ---------------------------------------------------------------------------------------------------------

export const NEEDS_ATTENTION_HEADLINE = 'A data source needs attention. The list below shows which one and why.';
export const UNKNOWN_HEADLINE = 'We could not confirm the status of a data source. Open Data Sources to check it.';
export const SETUP_TAKING_LONGER_MESSAGE = 'Connector setup is taking longer than expected. Please try again in a few minutes.';
export const SYNC_TAKING_LONGER_MESSAGE = 'Initial data sync is taking longer than expected. We\'ll keep working in the background — try again in a few minutes.';
export const AIRBYTE_SYNC_FAILED_MESSAGE = "A data source's last sync failed. Open Data Sources to see which connection needs attention.";
export const GENERIC_SETUP_MESSAGE = 'Unable to start setup. Please try again.';
export const WORKFLOW_NOT_ELIGIBLE_MESSAGE = 'Finish the earlier setup steps before starting your data.'; // v4.23 - H4-R2
export const INDUSTRY_CODE_MISSING_MESSAGE = 'We need your industry before we can process your data.'; // v4.26 - INDUSTRY-CODE-NOT-STORED S8

/** Retry caps per readiness reason (unchanged from DataSourceConnect v4.10). */
export const RETRY_CAP_MS: Readonly<Record<string, number>> = {
  AIRBYTE_SETUP_PENDING: 120_000,
  AIRBYTE_INITIAL_SYNC_PENDING: 1_200_000,
};

export const NON_RETRY_REASONS: ReadonlySet<string> = new Set([
  'AIRBYTE_SETUP_FAILED',
  'AIRBYTE_SYNC_FAILED',
  'NO_CONNECTIONS',
  'TENANT_NOT_FOUND',
  'DB_ERROR',
  'WORKFLOW_NOT_ELIGIBLE', // v4.23 - CONNECTOR-HEALTH-STATE-H4 (H4-R2)
  'INDUSTRY_CODE_MISSING', // v4.26 - INDUSTRY-CODE-NOT-STORED S8
  'UNKNOWN',
]);

const REASON_MESSAGES: Readonly<Record<string, string>> = {
  AIRBYTE_SETUP_FAILED: 'Connector setup failed. Reconnect the source and try again.',
  AIRBYTE_SYNC_FAILED: AIRBYTE_SYNC_FAILED_MESSAGE,
  NO_CONNECTIONS: 'No data sources connected. Connect at least one source before finishing setup.',
  TENANT_NOT_FOUND: 'Account not ready yet. Refresh and try again.',
  DB_ERROR: 'Service temporarily unavailable. Please try again in a moment.',
  AIRBYTE_SETUP_PENDING: SETUP_TAKING_LONGER_MESSAGE,
  AIRBYTE_INITIAL_SYNC_PENDING: SYNC_TAKING_LONGER_MESSAGE,
  WORKFLOW_NOT_ELIGIBLE: WORKFLOW_NOT_ELIGIBLE_MESSAGE, // v4.23 - H4-R2
  INDUSTRY_CODE_MISSING: INDUSTRY_CODE_MISSING_MESSAGE, // v4.26 - INDUSTRY-CODE-NOT-STORED S8
};

const HEADLINE_PRECEDENCE: readonly HealthState[] = ['needs_attention', 'setting_up', 'syncing', 'unknown'];
const HEADLINE_MESSAGES: Readonly<Partial<Record<HealthState, string>>> = {
  needs_attention: NEEDS_ATTENTION_HEADLINE,
  setting_up: SETUP_TAKING_LONGER_MESSAGE,
  syncing: SYNC_TAKING_LONGER_MESSAGE,
  unknown: UNKNOWN_HEADLINE,
};

export interface PendingConnectionView {
  id: string;
  label: string;
  healthState: HealthState;
  healthLabel: string;
  healthReason: string | null;
}

export interface FinishSetupDecision {
  action: 'retry' | 'stop';
  message: string;
  pending: PendingConnectionView[];
}

/**
 * Decide what Finish Setup (and PipelineProgress Retry) shows for a `not_ready` answer.
 * The headline is chosen by precedence over the pending connections' healthState
 * (needs_attention > setting_up > syncing > unknown), never by row order; needs_attention stops retrying even
 * when the reason is a pending one. With no pending connection the reason decides; an unknown reason falls to
 * the connection-based message, or the generic one when there is none.
 */
export function finishSetupDecision(
  result: { reason?: string | null; connectionsPending?: string[] | null },
  connections: ReadonlyArray<HealthCarrier>,
  elapsedMs: number,
): FinishSetupDecision {
  const reason = result.reason || 'UNKNOWN';
  const pending: PendingConnectionView[] = (result.connectionsPending || []).map(id => {
    const c = connections.find(x => x.id === id);
    const d = healthDisplay(c?.healthState, c?.healthReason ?? null);
    return { id, label: c?.sourceType || 'A data source', healthState: d.state, healthLabel: d.label, healthReason: d.reason };
  });
  const headline = HEADLINE_PRECEDENCE.find(s => pending.some(p => p.healthState === s));
  const headlineMessage = headline ? HEADLINE_MESSAGES[headline] : undefined;
  const reasonMessage = REASON_MESSAGES[reason];

  if (headline === 'needs_attention') {
    return { action: 'stop', message: NEEDS_ATTENTION_HEADLINE, pending };
  }
  const message = headlineMessage || reasonMessage || GENERIC_SETUP_MESSAGE;
  const cap = RETRY_CAP_MS[reason];
  if (NON_RETRY_REASONS.has(reason) || cap === undefined) {
    return { action: 'stop', message, pending };
  }
  return { action: elapsedMs >= cap ? 'stop' : 'retry', message, pending };
}
